import { FulfillmentGroupData } from "./fulfillment-group";
import {
  FulfillmentOrderLineItem,
  getLineItemGroupingKey,
  LineItemGroupingConfig,
} from "./fulfillment-order-line-item";

type ScanVerifiedItemized = FulfillmentGroupData["scanVerifiedItemized"];

export function getSerialNumbersByLineItemExternalId(
  scanVerifiedItemized: ScanVerifiedItemized,
): Map<string, string[]> {
  const byExternalId = new Map<string, string[]>();
  if (!scanVerifiedItemized) {
    return byExternalId;
  }

  for (const [externalId, scans] of Object.entries(scanVerifiedItemized)) {
    const serialNumbers = new Set<string>();
    for (const scan of scans ?? []) {
      for (const serialNumber of scan?.serialNumbers ?? []) {
        const trimmed = serialNumber?.trim();
        if (trimmed) {
          serialNumbers.add(trimmed);
        }
      }
    }
    if (serialNumbers.size > 0) {
      byExternalId.set(externalId, Array.from(serialNumbers));
    }
  }

  return byExternalId;
}

export function getAllSerialNumbers(
  scanVerifiedItemized: ScanVerifiedItemized,
): string[] {
  const serialNumbers = new Set<string>();
  for (const forLineItem of getSerialNumbersByLineItemExternalId(
    scanVerifiedItemized,
  ).values()) {
    for (const serialNumber of forLineItem) {
      serialNumbers.add(serialNumber);
    }
  }
  return Array.from(serialNumbers);
}

/** Expanded rows are one per unit, so the nth unit shows the nth serial. */
export function getSerialNumberForUnit(
  expandedItems: readonly { externalId: string }[],
  expansionIndex: number,
  scanVerifiedItemized: ScanVerifiedItemized,
): string {
  const item = expandedItems[expansionIndex];
  if (!item) {
    return "";
  }

  const serialNumbers = getSerialNumbersByLineItemExternalId(
    scanVerifiedItemized,
  ).get(item.externalId);
  if (!serialNumbers?.length) {
    return "";
  }

  let unitIndex = 0;
  for (let i = 0; i < expansionIndex; i++) {
    if (expandedItems[i]?.externalId === item.externalId) {
      unitIndex++;
    }
  }

  return serialNumbers[unitIndex] ?? "";
}

/** Pass the same config the caller grouped its rows with, or the keys won't line up. */
export function getSerialNumbersByLineItemGroupingKey(
  items: readonly FulfillmentOrderLineItem[],
  scanVerifiedItemized: ScanVerifiedItemized,
  config?: LineItemGroupingConfig,
): Map<string, string[]> {
  const byExternalId =
    getSerialNumbersByLineItemExternalId(scanVerifiedItemized);
  if (byExternalId.size === 0) {
    return new Map();
  }

  const byGroupingKey = new Map<string, Set<string>>();
  for (const item of items) {
    const serialNumbers = byExternalId.get(item.externalId);
    if (!serialNumbers?.length) {
      continue;
    }
    const key = getLineItemGroupingKey(item, config);
    let existing = byGroupingKey.get(key);
    if (!existing) {
      existing = new Set<string>();
      byGroupingKey.set(key, existing);
    }
    for (const serialNumber of serialNumbers) {
      existing.add(serialNumber);
    }
  }

  return new Map(
    Array.from(byGroupingKey, ([key, serialNumbers]) => [
      key,
      Array.from(serialNumbers),
    ]),
  );
}
